import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { BannerObjData } from "@hypexone/uicore/lib/cjs/src/models/Banners";
import { accountClient } from "../../../App.utils";
import { BRAND_DOMAIN } from "../../../constants/Branding";
import {
  DEFAULT_HOME_BANNERS,
  HOME_BANNER_CATEGORY,
} from "./homeBannerDefaults";

const DESKTOP_BREAKPOINT_PX = 768;

const dedupeBannersByUrl = (banners: BannerObjData[]) => {
  const seen = new Set<string>();
  return banners.filter((banner) => {
    if (seen.has(banner.publicUrl)) return false;
    seen.add(banner.publicUrl);
    return true;
  });
};

type HomeBannerCarouselProps = {
  /** HA banner management category (CmsPage id) to show. */
  category?: string;
  /** Shown when HA has nothing uploaded for the category. */
  defaults?: BannerObjData[];
};

const HomeBannerCarousel = ({
  category = HOME_BANNER_CATEGORY,
  defaults = DEFAULT_HOME_BANNERS,
}: HomeBannerCarouselProps) => {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= DESKTOP_BREAKPOINT_PX
  );
  const [bannersData, setBannersData] = useState<BannerObjData[]>([]);

  const fetchBanners = async () => {
    try {
      const response = await accountClient.getBannersByCategory(
        BRAND_DOMAIN,
        category
      );
      setBannersData(response?.banners ?? []);
    } catch {
      setBannersData([]);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, [category]);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`);
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const activeBanners = useMemo(() => {
    const defaultBanners = defaults.filter((b) =>
      isDesktop ? b.deviceType === "desktop" : b.deviceType === "mobile"
    );

    const desktopUploaded = dedupeBannersByUrl(
      bannersData.filter((b) => b.deviceType === "desktop")
    );
    const mobileUploaded = dedupeBannersByUrl(
      bannersData.filter((b) => b.deviceType === "mobile")
    );

    const uploadedBanners = isDesktop
      ? desktopUploaded.length > 0
        ? desktopUploaded
        : mobileUploaded
      : mobileUploaded.length > 0
        ? mobileUploaded
        : desktopUploaded;

    // HA banners when present; otherwise fall back to built-in defaults.
    return uploadedBanners.length > 0 ? uploadedBanners : defaultBanners;
  }, [bannersData, isDesktop, defaults]);

  const handleBannerClick = (banner: BannerObjData) => {
    const path = banner.redirectionUrl?.trim();
    if (!path || path.toLowerCase() === "nourl") return;
    navigate(path.startsWith("/") ? path : `/${path}`);
  };

  if (activeBanners.length === 0) return null;

  return (
    <div className="w-full bg-goldenYellow">
      <Slider
        key={isDesktop ? "desktop" : "mobile"}
        infinite={activeBanners.length > 1}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        autoplay={activeBanners.length > 1}
        autoplaySpeed={3000}
        arrows
        dots
        className="home-banner-slider"
      >
        {activeBanners.map((banner, index) => (
          <div key={`${banner?.id}-${banner?.publicUrl}`} className="w-full">
            <img
              src={banner?.publicUrl}
              alt={banner?.title || `banner-${index + 1}`}
              className="w-full h-auto cursor-pointer object-cover"
              onClick={() => handleBannerClick(banner)}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default HomeBannerCarousel;
